import { getReconDetailsRoute } from '@src/containers/Routes/routes';
import { useCurrentAppLayoutId } from '@src/hooks/useAppLayoutId';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  EMPTY_ARRAY,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  columnTypes,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useConstant,
  useDateRangeFilter,
  useMixpanel,
  usePersistedBlotterTable,
  useWSFilterPipe,
  useWsBlotterTable,
  type AgGridIconButtonProps,
  type ColumnOrColumnGroup,
  type FilterClause,
  type FilterableProperty,
  type ReconMismatch,
} from '@talos/kyoko';
import type { GridOptions } from 'ag-grid-community';
import { isEqual, pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { v1 as uuid } from 'uuid';
import { useResolveMismatchMenu } from './ReconMismatchMenu';
import { useReconMismatchColumns } from './useReconMismatchesColumns';
import {
  clientSideReconMismatchFilterCheck,
  useReconMismatchesFilter,
  type BlotterTableReconMismatchesFilter,
} from './useReconMismatchesFilter';

const getReconMismatchKey = (item: ReconMismatch) => item.rowID;

interface ReconMismatchesBlotterParams {
  blotterID: string;
  tabLabel?: string;
  defaultFilter: BlotterTableReconMismatchesFilter;

  onCloneTab: () => void;

  /** When mounting should the filter accordian be expanded */
  initialIsOpen?: boolean;
}

export function ReconMismatchesBlotter({
  blotterID,
  tabLabel,
  defaultFilter,
  onCloneTab,
  initialIsOpen,
}: ReconMismatchesBlotterParams) {
  const mixpanel = useMixpanel();
  const navigate = useNavigate();
  const layoutId = useCurrentAppLayoutId();
  const columns = useReconMismatchColumns({ defaultColumns: EMPTY_ARRAY });
  const persistence = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filtering = useReconMismatchesFilter({
    initialFilter: persistence.initialState?.filter,
    saveFilter: persistence.onFilterChanged,
  });

  const { changeFilter, filterableProperties, initialFilterClauses } = filtering;

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) as unknown as BlotterTableReconMismatchesFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const dateRangeFilter = useDateRangeFilter(filtering.filter, changeFilter);

  const blotterTag = useConstant(`RECON_MISMATCHES_${uuid()}`);

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const { getCustomContextMenuItems } = useResolveMismatchMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterableProperties,
  });

  const filterFunc = useCallback(
    (mismatch: ReconMismatch) => clientSideReconMismatchFilterCheck(mismatch, filtering.filter),
    [filtering.filter]
  );

  const filterPipe = useWSFilterPipe({ getUniqueKey: getReconMismatchKey, filterFunc });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        id: 'CustomGroup',
        title: 'Market Account',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const columnsWithButtons: ColumnOrColumnGroup[] = useMemo(() => {
    return [
      ...columns,
      {
        type: 'iconButton',
        id: 'mismatch-details',
        pinned: 'right',
        lockVisible: true,
        lockPinned: true,
        width: 60,
        params: {
          icon: IconName.Deepdive,
          onClick: params => {
            const mismatchIDs = params.node.data ? [params.node.data.ID] : [];
            navigate(getReconDetailsRoute({ mismatchIDs, layoutId }));
            mixpanel.track(MixpanelEvent.CreateTempReconMismatchDetailsTab, {
              [MixpanelEventProperty.Amount]: mismatchIDs.length,
            });
          },
          hide: params => {
            return !params.node.group;
          },
        } satisfies AgGridIconButtonProps<ReconMismatch>,
      },
    ];
  }, [columns, mixpanel, navigate, layoutId]);

  const request = useMemo(
    () => ({
      name: 'ReconMismatch',
      tag: blotterTag,
      ...removeEmptyFilters(onlyServerFilterKeys(filtering.filter)),
    }),
    [blotterTag, filtering.filter]
  );

  const gridOptions: GridOptions = useMemo(
    () => ({
      groupDisplayType: 'singleColumn',
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: false,
        enableClickSelection: true,
        groupSelects: 'filteredDescendants',
        checkboxLocation: 'autoGroupColumn',
      },
      groupHideParentOfSingleChild: 'leafGroupsOnly',
      autoGroupColumnDef,
    }),
    [autoGroupColumnDef]
  );

  const blotterTable = useWsBlotterTable({
    request,
    pipe: filterPipe,
    rowID: 'rowID' satisfies keyof ReconMismatch,
    columns: columnsWithButtons,
    persistence,
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions,
    exportOptions: {
      baseParams: { skipRowGroups: false },
      name: 'Reconciliation Mismatches',
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        {...dateRangeFilter}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="recon-mismatches-blotter-expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={onCloneTab}
            >
              Clone Tab
            </Button>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
    </>
  );
}

function onlyServerFilterKeys(filter: BlotterTableReconMismatchesFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, ['StartDate', 'EndDate', 'MarketAccounts', 'Symbols']);
}
